import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchRemovedDeclarations, restoreDeclaration } from '../api/declarationService';

function TableDeclarationsBin() {
  const [removedDeclarations, setRemovedDeclarations] = useState([]);
  const [message, setMessage] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(25);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restorePopup, setRestorePopup] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  
  const navigate = useNavigate();

  const loadRemoved = (currentPage) => {
    setIsPageLoading(true);
    
    fetchRemovedDeclarations(currentPage, perPage)
      .then(data => {
        if (!Array.isArray(data.items)) {
          console.error('Expected data.items to be an array, got:', data);
          setRemovedDeclarations([]);
          return;
        }
        setRemovedDeclarations(data.items);
        setPage(data.page);
        setTotalPages(data.pages);
      })
      .catch(err => {
        console.error('Error fetching bin declarations:', err);
        setMessage(`Error: ${err.message}`);
      })
      .finally(() => {
        setTimeout(() => {
          setIsPageLoading(false);
          
          const tableElement = document.querySelector('.declarations-bin-container');
          if (tableElement) {
            tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      });
  };

  useEffect(() => {
    loadRemoved(page);
  }, [page]);

  useEffect(() => {
    if (message) {
      setIsNotificationVisible(true);
      
      const timer = setTimeout(() => {
        setIsNotificationVisible(false);
        
        setTimeout(() => {
          setMessage('');
        }, 500);
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [message]);

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return 'N/A';
    
    const date = new Date(dateTimeStr);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRestore = (declId, companyName) => {
    setRestorePopup({ declId, companyName });
  };
    
  const confirmRestore = () => {
    if (!restorePopup) return;
    const { declId, companyName } = restorePopup;
    setIsRestoring(true);
    setMessage(`Restoring declaration for "${companyName}"...`);

    restoreDeclaration(declId)
      .then(data => {
        if (data.error) {
          console.error(data.error);
          setMessage(`Error restoring declaration: ${data.error}`);
        } else {
          setMessage(data.message || `Declaration for "${companyName}" restored successfully.`);
          loadRemoved(page);
        }
      })
      .catch(err => {
        console.error(err);
        setMessage(`Error restoring declaration: ${err.message}`);
      })
      .finally(() => {
        setIsRestoring(false);
        setRestorePopup(null);
      });
  };

  const handlePrevPage = () => {
    if (page > 1 && !isPageLoading) {
      setPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages && !isPageLoading) {
      setPage(prev => prev + 1);
    }
  };

  return (
    <div className="declarations-bin-container">
      <h2>Removed Declarations (Bin)</h2>
      
      {message && (
        <div 
          style={{ 
            position: 'fixed',
            top: 60,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '90%',
            maxWidth: '800px',
            zIndex: 1000,
            padding: '1rem',
            backgroundColor: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: '4px',
            boxShadow: '0px 4px 8px rgba(0,0,0,0.1)',
            opacity: isNotificationVisible ? 1 : 0,
            transition: 'opacity 0.5s ease-in-out'
          }}
        >
          {message}
        </div>
      )}
      
      <button 
        className="button-hide"
        onClick={() => navigate('/register1')} 
        style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center'}}
      >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        Back to Declarations
      </button>
      
      {isRestoring && (
        <div style={{ 
          marginTop: '1rem', 
          padding: '1rem', 
          backgroundColor: '#fff2e8', 
          border: '1px solid #ffbb96', 
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div style={{ marginRight: '10px' }}>Restoring declaration...</div>
          <div style={{ 
            width: '20px', 
            height: '20px', 
            border: '3px solid rgba(0, 0, 0, 0.1)', 
            borderRadius: '50%', 
            borderTop: '3px solid #3498db', 
            animation: 'spin 1s linear infinite' 
          }}></div>
        </div>
      )}

      <div style={{ 
        background: '#ffffff', 
        borderRadius: '8px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        position: 'relative'
      }}>
        {removedDeclarations.length === 0 ? (
          <div style={{ 
            padding: '2rem', 
            textAlign: 'center', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '4px',
            marginTop: '1rem'
          }}>
            No removed declarations found in the bin.
          </div>
        ) : (
          <>
            <div style={{ 
              marginBottom: '1rem', 
              padding: '0.5rem 1rem', 
              backgroundColor: '#fffbe6', 
              border: '1px solid #ffe58f', 
              borderRadius: '4px',
              fontSize: '14px'
            }}>
              Note: Declarations remain in the bin for 30 days after removal before being permanently deleted.
            </div>

            <div style={{ 
              overflowX: 'auto',
              overflowY: 'visible',
              maxWidth: '100%',
              position: 'relative'
            }}>
              {isPageLoading && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '1rem',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  zIndex: 10,
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div style={{ 
                    width: '30px',
                    height: '30px', 
                    border: '3px solid rgba(0, 0, 0, 0.1)', 
                    borderRadius: '50%', 
                    borderTop: '3px solid #1890ff', 
                    animation: 'spin 1s linear infinite' 
                  }}></div>
                </div>
              )}

              <table>
                <thead>
                  <tr style={{ backgroundColor: '#fafafa', borderBottom: '2px solid #f0f0f0' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Aktenzeichen</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Publication Date</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Company Name</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Title & Name of Agent</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Removed At</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>Restore</th>
                  </tr>
                </thead>
                <tbody>
                  {removedDeclarations.map((decl, index) => (
                    <tr key={decl.declaration_id} style={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <td style={{ padding: '12px 16px' }}>{decl.aktenzeichen}</td>
                      <td style={{ padding: '12px 16px' }}>{decl.publication_date}</td>
                      <td style={{ padding: '12px 16px' }}>{decl.company_name}</td>
                      <td style={{ padding: '12px 16px' }}>{`${decl.agent_title || ''} ${decl.agent_name || ''}`.trim()}</td>
                      <td style={{ padding: '12px 16px' }}>{formatDateTime(decl.removed_at)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <button 
                          onClick={() => handleRestore(decl.declaration_id, decl.company_name)}
                          disabled={isRestoring}
                          title="Restore declaration"
                          style={{
                            background: '#e6f7ff',
                            border: '1px solid #91d5ff',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            padding: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#1890ff'
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#1890ff" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {restorePopup && (
        <div className="popup-overlay" >
          <div className="popup-content">
            <h3>Restore Declaration</h3>
            <p>Are you sure you want to restore the declaration for "{restorePopup.companyName}"?</p>
            <div className="popup-actions">
              <button 
                className="button-accept" 
                onClick={confirmRestore} 
                disabled={isRestoring}
              >
                Restore
              </button>
              <button 
                className="button-cancel" 
                onClick={() => setRestorePopup(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pagination-container">
        <button
          className="pagination-button"
          disabled={page <= 1 || isPageLoading}
          onClick={() => setPage(1)}
          title="First Page"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>

        <button
          className="pagination-button"
          onClick={handlePrevPage}
          disabled={page <= 1 || isPageLoading}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>

        <div style={{ 
          padding: '8px 16px',
          borderRadius: '4px',
          background: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <span>Page {page} of {totalPages || 1}</span>
        </div>

        <button
          className="pagination-button"
          onClick={handleNextPage}
          disabled={page >= totalPages || totalPages === 0 || isPageLoading}
        >
          Next
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <button
          className="pagination-button"
          disabled={page >= totalPages || totalPages === 0 || isPageLoading}
          onClick={() => setPage(totalPages)}
          title="Last Page"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7m-8-14l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default TableDeclarationsBin;